import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from '../../Utils/debouncer';
import ViewportContext from './ViewportContext';
import { getViewport } from './util';

/**
Provider per la gestione del viewport.

Il provider si mette in ascolto del resize e comunica ai consumer le modifiche della viewport.

vedi `useViewport`

vedi `ViewportContext`

vedi `usePluginAppViewport`
 */
const ViewportProvider = ({ children, container }) => {
  const [size, setSize] = useState(getViewport(container || window));

  useEffect(() => {
    const w = container || window;
    const handleViewportResize = debounce(() => {
      setSize((s) => {
        const newViewport = getViewport(w);
        if (newViewport.width === s.width && newViewport.height === s.height) return s;
        return newViewport;
      });
    });

    w.addEventListener('resize', handleViewportResize);
    return () => w.removeEventListener('resize', handleViewportResize);
  }, [container]);

  return (
    <ViewportContext.Provider value={size}>{children}</ViewportContext.Provider>
  );
};

ViewportProvider.propTypes = {
  /**
  Il container della viewport (di default è `window`).

  __Quando è necessario cambiare il container?__

  In alcuni situazioni la viewport non è gestita dalla pagina corrente (`window`), ma da altre.
  Un caso tipico è l'utilizzo di IFrame.
  In questo caso il container è la finestra chiamante e quindi a questa finstra spetta il calcolo della viewport.
   */
  container: PropTypes.shape({
    innerHeight: PropTypes.number.isRequired,
    innerWidth: PropTypes.number.isRequired,
    addEventListener: PropTypes.func.isRequired,
    removeEventListener: PropTypes.func.isRequired,
  }),

  /**
   * Children.
   */
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

ViewportProvider.defaultProps = {
  container: undefined,
};

export default ViewportProvider;
